import { bootstrapHubClient } from 'agiohub-js'
import { friendlyAppName } from './app-display-names.js'

const HUB_URL = import.meta.env.VITE_AGIOHUB_HUB_URL
const APP_TOKEN = import.meta.env.VITE_AGIOHUB_APP_TOKEN

if (!HUB_URL || !APP_TOKEN) {
  // Bootstrap requires both. Fail loud — without them every other module that
  // imports `auth` would silently break later. See docs/manual/06-desarrollo.md §11.
  const missing = [!HUB_URL && 'VITE_AGIOHUB_HUB_URL', !APP_TOKEN && 'VITE_AGIOHUB_APP_TOKEN']
    .filter(Boolean)
    .join(', ')
  throw new Error(
    `[apps/web] bootstrap requires ${missing}. Set them in apps/web/.env. ` +
      `Provision a token via: scripts/seed_app_credentials.py`,
  )
}

const hub = await bootstrapHubClient({
  hubUrl: HUB_URL,
  appToken: APP_TOKEN,
  // Inc 3.2: modo cookie explícito. El SDK consume las cookies HttpOnly
  // `agio_session` + `agio_csrf` que el backend Inc 3.1 setea en login.
  // Sin tokens en localStorage (excepto refresh + user para resiliencia).
  storage: 'cookie',
  hooks: {
    onPasswordChangeRequired() {
      if (!window.location.pathname.includes('/change-password')) {
        window.location.href = '/pages/change-password.html'
      }
    },
    onUnauthenticated(reason) {
      // Inc 2.3: en vez de redirigir directo, dispatch evento global que
      // el shell de `@agiohub/shell` escucha (toast + redirect con delay).
      // El bootstrap NO redirige — el shell decide UX. Esto desacopla el
      // flujo de "sesión expirada" del SDK del manejo visual.
      //
      // Si no hay shell montado (ej. página pre-auth como /index.html),
      // hacemos fallback con redirect directo después de ~1500ms para no
      // dejar al user en limbo.
      window.dispatchEvent(
        new CustomEvent('agio:session-expired', {
          detail: { reason: reason || 'unauthenticated' },
        }),
      )
      // Fallback: si nadie maneja el evento (ej. página pre-auth), redirect.
      setTimeout(() => {
        if (!window.location.pathname.includes('/index.html')) {
          window.location.href = '/index.html'
        }
      }, 1800)
    },
    async beforeLogout() {
      try {
        const { portalPrefs } = await import('./portal-prefs.js')
        await portalPrefs.flushPending()
      } catch (_) {}
      try { localStorage.removeItem('portal:prefs') } catch (_) {}
    },
    onBootstrapFailed(error) {
      // Avoid a redirect loop if we're already on the error page.
      if (window.location.pathname.includes('/index.html') || window.location.pathname === '/') {
        console.error('[apps/web] Bootstrap failed:', error)
        return
      }
      window.location.href = `/index.html?error=bootstrap&code=${encodeURIComponent(error.code)}`
    },
  },
})

// Nombres amigables del launcher (solo frontend). Envolvemos los lectores del
// catálogo de apps para sustituir el slug técnico (`name` del backend) por una
// etiqueta legible. Único punto: cubre tarjetas del launcher, command palette y
// pins del sidebar del shell de forma consistente. `slug` se conserva intacto
// (las mutaciones add/remove y la lógica de pins siguen usándolo). Envuelto en
// try/catch para degradar a los nombres del backend si el cliente no permite
// reasignar estos métodos.
try {
  const appsList = hub?.apps?.list?.bind(hub.apps)
  if (appsList) {
    hub.apps.list = async (...args) => {
      const rows = await appsList(...args)
      return Array.isArray(rows)
        ? rows.map((r) => ({ ...r, name: friendlyAppName(r.slug, r.name) }))
        : rows
    }
  }
  const pinnedList = hub?.apps?.pinned?.list?.bind(hub.apps.pinned)
  if (pinnedList) {
    hub.apps.pinned.list = async (...args) => {
      const pins = await pinnedList(...args)
      return Array.isArray(pins)
        ? pins.map((p) => ({ ...p, name: friendlyAppName(p.slug, p.name) }))
        : pins
    }
  }
} catch (err) {
  console.warn('[apps/web] no se pudieron envolver los nombres de app del launcher', err)
}

// Entorno Odoo del usuario (prod o pruebas): separa lo que el navegador guarda
// por entorno y avisa en pantalla si es de pruebas. Best-effort, no bloquea.
try {
  if (hub?.auth?.isAuthenticated?.()) {
    import('./odoo-entorno.js').then(({ syncOdooEntorno }) => syncOdooEntorno(hub.client))
  }
} catch (_) {}

export const auth = hub.auth
export const client = hub.client
export const preferences = hub.preferences
export const secrets = hub.secrets
export const appInfo = hub.appInfo
// Re-export del cliente completo para servicios que consumen `hub.apps`
// (catálogo + pinned). Mantener `auth`/`client`/`preferences` exports por
// compatibilidad con el resto del web.
export { hub }
